/**
 * Parent packet public UI: submit gating + phone format + validation messages.
 * Phone rules extend beyond legacy structural canSubmit (Fay feedback): required slots must be
 * non-empty with exactly 10 digits after stripping non-digits; optional phones allow blank, else 10 digits.
 */
import { getPacketSiteSlug } from './NaToggle';
import {
  consentAckDeclineSatisfied,
  fa09RemoteLearningSatisfied,
  isPhiAuthorizationForm,
  pushConsentAckDecline,
  pushRequiredConsentAck,
  pushRequiredYesNo,
  yesNoAnswered,
} from './parentPacketConsent';
import { confirmAgreedValidationMessage } from './parentPacketConfirmLabel';
import {
  inferFieldKind,
  stripDigits as stripTypedDigits,
  validatePhoneValue,
  validateTypedField,
} from './packetFieldValidation';

export { stripDigits } from './packetFieldValidation';

const FORM08_REQUIRED_YN_KEYS = [
  ['visual_impairment', 'Visual impairment'],
  ['glasses', 'Glasses'],
  ['ear_infection', 'Ear infection history'],
  ['ear_tubes', 'Ear tubes'],
  ['hearing_impairment', 'Hearing impairment'],
  ['assistive_hearing_device', 'Assistive hearing device'],
  ['seizure', 'Seizure history'],
  ['gtube', 'G-tube'],
  ['can_have_fluids', 'Can have fluids'],
  ['upper_respiratory_infection', 'History of upper respiratory infection'],
  ['asthma', 'Asthma'],
  ['respiratory_compromise', 'Respiratory compromise'],
  ['cardiac_compromise', 'Cardiac compromise'],
  ['nebulizer_treatments', 'Receives nebulizer treatments'],
  ['can_sun_exposure', 'Can be exposed to the sun'],
  ['history_diarrhea', 'History of diarrhea'],
  ['history_constipation', 'History of constipation'],
  ['dislocation_history', 'History of elbow/shoulder dislocation'],
  ['wears_brace_orthotic', 'Wears brace/orthotic'],
  ['wound_care', 'Receives wound care/bandage changes'],
  ['wears_helmet', 'Wears helmet during physical activity'],
  ['skin_condition', 'Skin condition'],
  ['dental_condition', 'Dental condition'],
  ['tracheostomy', 'Tracheostomy'],
  ['oxygen', 'Oxygen'],
  ['oximeter_monitoring', 'Oximeter monitoring'],
  ['problems_eating_drinking', 'Problems with eating or drinking'],
];

/** 14th Ave informational parent-info forms requiring inline read-review ack (2026-06-18). */
const FOURTEENTH_AVE_INLINE_ACK_FORMS = new Set(['fa_01', 'fa_02', 'fa_15']);

export function fourteenthAveInfoAckRequired(formId, siteSlug) {
  return siteSlug === '14th-ave' && FOURTEENTH_AVE_INLINE_ACK_FORMS.has(formId);
}

/** Normalize saved fa_03 rows (legacy `name` → first/last) for UI + validation. */
export function normalizeFa03BusRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    if (!r || typeof r !== 'object') {
      return { first_name: '', last_name: '', relationship: '', address: '', phone: '' };
    }
    let fn = (r.first_name || '').trim();
    let ln = (r.last_name || '').trim();
    if ((!fn || !ln) && (r.name || '').trim()) {
      const parts = (r.name || '').trim().split(/\s+/).filter(Boolean);
      if (!fn) fn = parts[0] || '';
      if (!ln) ln = parts.slice(1).join(' ') || '';
    }
    return {
      first_name: fn,
      last_name: ln,
      relationship: (r.relationship || '').trim(),
      address: (r.address || '').trim(),
      phone: (r.phone || '').trim(),
    };
  });
}

/** Rows 1–2 (indices 0–1): first + last + phone, or legacy name (2+ tokens) + phone. */
export function fa03BothRowsComplete(rows) {
  const r = rows || [];
  for (let i = 0; i < 2; i += 1) {
    const row = r[i] || {};
    const fn = (row.first_name || '').trim();
    const ln = (row.last_name || '').trim();
    const ph = (row.phone || '').trim();
    if (fn && ln && ph) continue;
    const legacy = (row.name || '').trim();
    const parts = legacy.split(/\s+/).filter(Boolean);
    if (legacy && ph && parts.length >= 2) continue;
    return false;
  }
  return true;
}

/** @deprecated Use fa03BothRowsComplete — kept as alias for older imports/tests. */
export const fa03FirstThreeRowsComplete = fa03BothRowsComplete;

// stripTypedDigits re-exported from packetFieldValidation.js for other modules

export function packetFieldId(suffix) {
  return `packet-field-${suffix}`;
}

/**
 * Yes/No keys that must be answered ('yes' or 'no') on submit for the 14th Ave
 * new-student intake forms. Unanswered ('' / undefined) blocks submit. Order
 * here drives the firstErrorId for scroll-to-error.
 */
export const FA16_REQUIRED_YESNO_KEYS = [
  'seizures_yn',
  'sleep_takes_nap',
  'sleep_through_night',
  'sleep_toilet_accidents',
  'comm_phrases',
  'comm_sentences',
  'comm_directions',
  'comm_responds_name',
  'comm_sign_language',
  'outside_unattended',
  'play_occupies_self',
  'play_with_other_children',
  'play_with_adults',
  'play_appropriately_toys',
  'seeks_attention',
  'shares_attention',
];

export const FA17_REQUIRED_YESNO_KEYS = [
  'feeding_refuses_food',
  'feeding_vomits_after',
  'feeding_vomits_during',
  'feeding_takes_long',
  'feeding_difficulty_liquids',
  'feeding_difficulty_solids',
  'feeding_food_spills',
  'feeding_cries_during',
  'feeding_cries_after',
  'feeding_needs_force_feeding',
  'feeding_drools_frequently',
  'feeding_coughs_when_eating',
  'feeding_chokes_when_eating',
];

/** fa_12 flat UI keys that must be answered yes/no before submit (paper form Y/N grid). */
export const FA12_REQUIRED_YESNO_KEYS = [
  'seizures_yn',
  'feed_refuses_food_yn',
  'feed_vomits_after_yn',
  'feed_vomits_during_yn',
  'feed_long_time_yn',
  'feed_liquids_difficulty_yn',
  'feed_solids_difficulty_yn',
  'feed_food_spills_yn',
  'feed_cries_during_yn',
  'feed_cries_after_yn',
  'feed_force_fed_yn',
  'feed_drools_yn',
  'feed_coughs_yn',
  'feed_chokes_yn',
  'naps_yn',
  'sleeps_through_yn',
  'toilet_accidents_yn',
  'phrases_yn',
  'sentences_yn',
  'directions_yn',
  'name_yn',
  'sign_yn',
  'goes_outside_yn',
  'occupies_self_yn',
  'plays_other_children_yn',
  'plays_adults_yn',
  'plays_toys_yn',
  'seek_attention_yn',
  'share_attention_yn',
];

export const FA12_YESNO_NA_ALLOWED_KEYS = new Set(['sign_yn']);

/** Core fa_12 text fields (full Fay matrix blocked on markup PDF). */
export const FA12_REQUIRED_TEXT_FIELDS = [
  ['person_completing', 'Person completing form is required'],
  ['sex', 'Sex (M/F) is required'],
  ['address', 'Address is required'],
  ['zip', 'ZIP code is required'],
  ['phone', 'Phone is required'],
  ['emergency_name', 'Emergency contact name is required'],
  ['emergency_phone', 'Emergency contact phone is required'],
  ['language_home', 'Language spoken at home is required'],
];

export function isYesNoAnswered(value) {
  return value === 'yes' || value === 'no';
}

/**
 * fa_16 Yes/No keys that ALSO accept 'na' as a satisfied answer. Currently
 * a single key: Communication → "Uses sign language" — a parent without a
 * deaf/hard-of-hearing context needs N/A as a legitimate third choice.
 * Every other required Y/N key on both forms is strict 'yes' | 'no'.
 */
export const FA16_YESNO_NA_ALLOWED_KEYS = new Set(['comm_sign_language']);

export function isRequiredYesNoSatisfied(key, value) {
  if (FA16_YESNO_NA_ALLOWED_KEYS.has(key) || FA12_YESNO_NA_ALLOWED_KEYS.has(key)) {
    return value === 'yes' || value === 'no' || value === 'na';
  }
  return isYesNoAnswered(value);
}

/** form_10 (Child Identification): required text fields; nickname optional. */
export const FORM10_REQUIRED_TEXT_FIELDS = [
  'hair_color',
  'eye_color',
  'glasses',
  'physical_description',
  'usual_behavior',
  'communication_ability',
  'favorite_locations',
];

const FORM10_REQUIRED_TEXT_MESSAGES = {
  hair_color: "Please enter your child's hair color.",
  eye_color: "Please enter your child's eye color.",
  glasses: "Please indicate whether your child wears glasses.",
  physical_description: "Please provide your child's physical description.",
  usual_behavior: "Please describe your child's usual behavior.",
  communication_ability: "Please describe your child's communication ability.",
  favorite_locations: "Please list your child's favorite locations.",
};

export const FA08_AFFIRM_KEYS = [
  'consent_video',
  'consent_video_behind',
  'consent_audio',
  'consent_photo',
  'consent_photo_blurred',
  'consent_social_media',
];

export function fa08HasAffirmative(fields) {
  return FA08_AFFIRM_KEYS.some((k) => !!fields[k]);
}

export function isNonBlankText(value) {
  return String(value ?? '').trim().length > 0;
}

/** form_14: slot idx 1–3 is complete when name + relationship + 10-digit phone are present. */
export function form14PickupSlotComplete(fields, idx) {
  const name = fields?.[`p${idx}_name`];
  const relationship = fields?.[`p${idx}_relationship`];
  const phone = fields?.[`p${idx}_phone`];
  return (
    isNonBlankText(name)
    && isNonBlankText(relationship)
    && stripTypedDigits(phone).length === 10
  );
}

export function form14PickupSlotStarted(fields, idx) {
  return (
    isNonBlankText(fields?.[`p${idx}_name`])
    || isNonBlankText(fields?.[`p${idx}_phone`])
    || isNonBlankText(fields?.[`p${idx}_relationship`])
  );
}

/** At least one complete authorized pickup; any started slot must be complete. */
export function form14PickupSatisfied(fields) {
  const anyComplete = [1, 2, 3].some((i) => form14PickupSlotComplete(fields, i));
  if (!anyComplete) return false;
  return [1, 2, 3].every((i) => {
    if (!form14PickupSlotStarted(fields, i)) return true;
    return form14PickupSlotComplete(fields, i);
  });
}

/** form_09: when has_allergies, require ≥1 allergen row. */
export function form09AllergyDetailsSatisfied(fields) {
  if (fields?.has_no_allergies) return true;
  if (!fields?.has_allergies) return false;
  const rows = fields?.allergy_rows || [];
  if (!Array.isArray(rows)) return false;
  return rows.some((r) => r && isNonBlankText(r.allergen));
}

export function form09AllergyChoiceSatisfied(fields) {
  return !!fields?.has_allergies || !!fields?.has_no_allergies;
}

function form07PreferredPhoneSatisfied(fields) {
  const prefDigits = stripTypedDigits(fields.preferred_phone || '');
  const motherDigits = stripTypedDigits(fields.mother_cell || '');
  const fatherDigits = stripTypedDigits(fields.father_cell || '');
  if (prefDigits.length === 10) return true;
  if (prefDigits.length > 0) return false;
  return motherDigits.length === 10 || fatherDigits.length === 10;
}

export function resolveForm07PreferredPhone(fields) {
  const prefDigits = stripTypedDigits(fields.preferred_phone || '');
  if (prefDigits.length === 10) return fields.preferred_phone || '';
  const mother = (fields.mother_cell || '').trim();
  const father = (fields.father_cell || '').trim();
  if (stripTypedDigits(mother).length === 10) return mother;
  if (stripTypedDigits(father).length === 10) return father;
  return fields.preferred_phone || '';
}

function form07RequiredFieldsValid(fields, { showMother = true } = {}) {
  if (showMother && !isNonBlankText(fields.mother_name)) return false;
  const hasCell = !!(fields.mother_cell?.trim() || fields.father_cell?.trim());
  if (!hasCell) return false;
  if (hasCell) {
    if (fields.mother_cell?.trim() && stripTypedDigits(fields.mother_cell).length !== 10) return false;
    if (fields.father_cell?.trim() && stripTypedDigits(fields.father_cell).length !== 10) return false;
  }
  if (!isNonBlankText(fields.email)) return false;
  if (!isNonBlankText(fields.ec1_name)) return false;
  if (!isNonBlankText(fields.ec1_phone) || stripTypedDigits(fields.ec1_phone).length !== 10) return false;
  if (!isNonBlankText(fields.ec2_name)) return false;
  if (!isNonBlankText(fields.ec2_phone) || stripTypedDigits(fields.ec2_phone).length !== 10) return false;
  if (!form07PreferredPhoneSatisfied(fields)) {
    return false;
  }
  return true;
}

function pushTypedField(errors, fieldErrorMap, fieldKey, value, label, { required = false, kind = null } = {}) {
  const err = validateTypedField(fieldKey, value, label, { required, kind });
  if (!err) return;
  const id = packetFieldId(fieldKey);
  errors.push({ id, fieldKey, message: err.message });
  fieldErrorMap[fieldKey] = err.message;
}

function pushRequiredPhone(errors, fieldErrorMap, raw, label, fieldKey) {
  const err = validatePhoneValue(raw, { required: true, label });
  if (!err) return;
  const id = packetFieldId(fieldKey);
  errors.push({ id, fieldKey, message: err.message });
  fieldErrorMap[fieldKey] = err.message;
}

export function form10RequiredTextValid(fields) {
  return FORM10_REQUIRED_TEXT_FIELDS.every((key) => isNonBlankText(fields[key]));
}

/**
 * Structural submit readiness only (matches legacy canSubmit; no phone digit count).
 */
const BS08_CONSENT_KEYS = [
  'consent_video',
  'consent_audio',
  'consent_photo',
  'consent_school_use',
  'consent_website',
];
const BS08_CONSENT_REQUIRED_MESSAGE = 'Select at least one photo/video consent option, or choose I DO NOT consent';
const WM05_CONSENT_KEYS = [
  'consent_video',
  'consent_audio',
  'consent_photo',
];

function bs08HasAffirmative(fields) {
  return BS08_CONSENT_KEYS.some((k) => !!fields[k]);
}

function bs08ConsentSatisfied(fields) {
  const anyAffirm = bs08HasAffirmative(fields);
  if (anyAffirm && fields.consent_none) return false;
  return anyAffirm || !!fields.consent_none;
}

export function submitFieldsValid(formId, fields, packet = null) {
  if (!formId) return true;
  if (formId === 'form_16' || formId === 'form_17') return true;
  if (formId === 'bs_08') {
    return !!fields.confirm_agreed && bs08ConsentSatisfied(fields);
  }
  if (formId === 'form_11' || formId === 'form_12') {
    if (formId === 'form_12' && !consentAckDeclineSatisfied(fields, 'creams_consent_ack', 'creams_consent_decline')) {
      return false;
    }
    return !!fields.confirm_agreed;
  }
  if (formId === 'form_13') {
    // A2P SMS/voice boxes are optional — declining either/both never blocks submit.
    return !!fields.confirm_agreed;
  }
  if (formId === 'form_15') {
    return consentAckDeclineSatisfied(fields, 'release_consent_ack', 'release_consent_decline')
      && !!fields.confirm_agreed;
  }
  if (isPhiAuthorizationForm(formId)) {
    const opwdd = (fields.opwdd_eligible || '').toString().toLowerCase();
    const respite = (fields.receiving_respite || '').toString().toLowerCase();
    const contact = (fields.contact_after_school || '').toString().toLowerCase();
    return consentAckDeclineSatisfied(fields, 'phi_authorize', 'phi_decline')
      && (opwdd === 'yes' || opwdd === 'no' || opwdd === 'unsure')
      && (respite === 'yes' || respite === 'no')
      && (contact === 'yes' || contact === 'no')
      && !!fields.confirm_agreed;
  }
  if (formId === 'form_18') {
    return !!fields.confirm_policy && !!fields.confirm_agreed;
  }
  if (formId === 'form_19') {
    const baseOk = !!fields.handbook_received_ack && !!fields.handbook_illness_policy_ack;
    if (getPacketSiteSlug(packet) === '55th-street') return baseOk;
    return baseOk && !!fields.handbook_therapy_makeup_ack;
  }
  if (formId === 'form_09') {
    const yn = (fields.food_from_home_yn || '').toString().toLowerCase();
    return !!fields.confirm_agreed
      && form09AllergyChoiceSatisfied(fields)
      && !(fields.has_allergies && fields.has_no_allergies)
      && (yn === 'yes' || yn === 'no')
      && form09AllergyDetailsSatisfied(fields);
  }
  if (formId === 'form_14') {
    return !!fields.confirm_agreed && form14PickupSatisfied(fields);
  }
  if (typeof formId === 'string' && formId.startsWith('fa_')) {
    if (formId === 'fa_03') {
      const rows = normalizeFa03BusRows(fields.bus_pickup_rows || []);
      return !!fields.confirm_agreed && fa03BothRowsComplete(rows);
    }
    if (formId === 'fa_04') {
      const baseOk = !!fields.confirm_agreed
        && isNonBlankText(fields.pediatrician_name)
        && stripTypedDigits(fields.pediatrician_phone).length === 10
        && isNonBlankText(fields.allergies)
        && !!fields.emergency_treatment_ack
        && !!(fields.mother_cell?.trim() || fields.father_cell?.trim())
        && isNonBlankText(fields.email);
      if (!baseOk) return false;
      const dentistPhone = fields.dentist_phone;
      if (dentistPhone != null && dentistPhone !== '' && stripTypedDigits(dentistPhone).length !== 10) {
        return false;
      }
      const is55th = getPacketSiteSlug(packet) === '55th-street';
      return [1, 2, 3].every((idx) => {
        const name = fields[`ec${idx}_name`];
        const relationship = fields[`ec${idx}_relationship`];
        const phone = fields[`ec${idx}_phone`];
        const started = isNonBlankText(name) || isNonBlankText(relationship) || isNonBlankText(phone);
        const mustComplete = idx === 1 || is55th || started;
        if (!mustComplete) {
          return phone == null || phone === '' || stripTypedDigits(phone).length === 10;
        }
        return isNonBlankText(name)
          && isNonBlankText(relationship)
          && stripTypedDigits(phone).length === 10;
      });
    }
    if (formId === 'fa_05') {
      return !!fields.confirm_agreed
        && consentAckDeclineSatisfied(fields, 'otc_consent_ack', 'otc_consent_decline');
    }
    if (formId === 'fa_06') return !!fields.confirm_agreed && !!fields.understands_labeled_bottles;
    if (formId === 'fa_07') {
      const dentistPhoneOk = fields.dentist_phone == null || fields.dentist_phone === ''
        || stripTypedDigits(fields.dentist_phone).length === 10;
      return !!fields.confirm_agreed
        && isNonBlankText(fields.pediatrician_name)
        && stripTypedDigits(fields.pediatrician_phone).length === 10
        && dentistPhoneOk
        && consentAckDeclineSatisfied(fields, 'doctor_comm_ack', 'doctor_comm_decline')
        && consentAckDeclineSatisfied(fields, 'release_info_ack', 'release_info_decline');
    }
    if (formId === 'fa_08') {
      const anyAffirm = fa08HasAffirmative(fields);
      if (anyAffirm && fields.consent_none) return false;
      return !!fields.confirm_agreed && (anyAffirm || !!fields.consent_none);
    }
    if (formId === 'fa_09') {
      return !!fields.confirm_agreed && fa09RemoteLearningSatisfied(fields);
    }
    if (formId === 'fa_10') {
      return !!fields.confirm_agreed
        && consentAckDeclineSatisfied(fields, 'trips_ack', 'trips_decline');
    }
    if (formId === 'fa_12') {
      if (!fields.confirm_agreed) return false;
      if (!FA12_REQUIRED_YESNO_KEYS.every((k) => isRequiredYesNoSatisfied(k, fields[k]))) return false;
      return FA12_REQUIRED_TEXT_FIELDS.every(([key]) => isNonBlankText(fields[key]));
    }
    if (formId === 'fa_16') {
      if (!fields.confirm_agreed) return false;
      return FA16_REQUIRED_YESNO_KEYS.every((k) => isRequiredYesNoSatisfied(k, fields[k]));
    }
    if (formId === 'fa_17') {
      if (!fields.confirm_agreed) return false;
      return FA17_REQUIRED_YESNO_KEYS.every((k) => isRequiredYesNoSatisfied(k, fields[k]));
    }
    return !!fields.confirm_agreed;
  }
  if (formId === 'form_10') {
    return form10RequiredTextValid(fields) && !!fields.confirm_agreed;
  }
  if (formId === 'form_07') {
    const p = packet || {};
    const showMother = p.has_mother !== false;
    return !!fields.confirm_agreed && form07RequiredFieldsValid(fields, { showMother });
  }
  if (formId === 'wm_01') {
    return !!fields.confirm_agreed
      && isNonBlankText(fields.parent_name)
      && isNonBlankText(fields.student_name)
      && isNonBlankText(fields.alert_cell_phone)
      && isNonBlankText(fields.email);
  }
  if (formId === 'wm_03') {
    if (!fields.confirm_agreed) return false;
    const genderOk = fields.gender === 'male' || fields.gender === 'female';
    if (!genderOk) return false;
    const addressOk = isNonBlankText(fields.home_address)
      && isNonBlankText(fields.home_city)
      && isNonBlankText(fields.home_state)
      && isNonBlankText(fields.home_zip);
    if (!addressOk) return false;
    const hasCell = !!(fields.mother_cell?.trim() || fields.father_cell?.trim());
    if (!hasCell) return false;
    const hasEmail = !!(fields.email?.trim() || fields.father_email?.trim());
    if (!hasEmail) return false;
    const bothEmails = !!(fields.email?.trim() && fields.father_email?.trim());
    if (bothEmails && !['mother', 'father', 'both'].includes(fields.docuseal_recipient)) return false;
    return [1, 2, 3].every((idx) => {
      const name = fields[`ec${idx}_name`];
      const relationship = fields[`ec${idx}_relationship`];
      const homePhone = fields[`ec${idx}_home_phone`];
      const cellPhone = fields[`ec${idx}_cell_phone`];
      const started = isNonBlankText(name) || isNonBlankText(relationship)
        || isNonBlankText(homePhone) || isNonBlankText(cellPhone);
      if (idx !== 1 && !started) return true;
      return isNonBlankText(name)
        && isNonBlankText(relationship)
        && (isNonBlankText(homePhone) || isNonBlankText(cellPhone));
    });
  }
  if (formId === 'wm_04') {
    const genderOk = fields.gender === 'male' || fields.gender === 'female';
    return !!fields.confirm_agreed
      && genderOk
      && isNonBlankText(fields.student_name)
      && isNonBlankText(fields.home_address)
      && isNonBlankText(fields.insurance_name)
      && isNonBlankText(fields.ec1_name)
      && isNonBlankText(fields.ec1_relationship)
      && isNonBlankText(fields.parent_name);
  }
  if (formId === 'wm_05') {
    const anyAffirm = WM05_CONSENT_KEYS.some((k) => !!fields[k]);
    if (anyAffirm && fields.consent_none) return false;
    return !!fields.confirm_agreed && (anyAffirm || !!fields.consent_none);
  }
  if (formId === 'wm_06') {
    return !!fields.confirm_agreed;
  }
  if (formId === 'wm_07') {
    if (!fields.confirm_agreed) return false;
    if (fields.no_medications) return true;
    return isNonBlankText(fields.medication_name)
      && isNonBlankText(fields.medication_dosage)
      && isNonBlankText(fields.medication_frequency);
  }
  if (formId === 'wm_10') {
    const genderOk = fields.gender === 'male' || fields.gender === 'female';
    const baseOk = !!fields.confirm_agreed
      && isNonBlankText(fields.child_last_name)
      && isNonBlankText(fields.child_first_name)
      && isNonBlankText(fields.home_address)
      && isNonBlankText(fields.home_city)
      && isNonBlankText(fields.home_zip)
      && isNonBlankText(fields.date_of_birth)
      && genderOk
      && isNonBlankText(fields.auth1_name)
      && stripTypedDigits(fields.auth1_cell).length === 10
      && isNonBlankText(fields.auth1_vehicle)
      && isNonBlankText(fields.auth1_plate);
    if (!baseOk) return false;
    if (packet?.has_mother !== false) {
      if (!isNonBlankText(fields.mother_name) || stripTypedDigits(fields.mother_cell).length !== 10) return false;
    }
    if (packet?.has_father !== false) {
      if (!isNonBlankText(fields.father_name) || stripTypedDigits(fields.father_cell).length !== 10) return false;
    }
    return true;
  }
  if (formId === 'wm_12') {
    return !!fields.confirm_agreed && isNonBlankText(fields.allergies);
  }
  if (formId === 'wm_09' || formId === 'wm_13') {
    return !!fields.confirm_agreed
      && consentAckDeclineSatisfied(fields, 'medicaid_consent_ack', 'medicaid_consent_decline');
  }
  if (typeof formId === 'string' && formId.startsWith('wm_')) {
    return !!fields.confirm_agreed;
  }
  return !!fields.confirm_agreed;
}

function pushPhoneOptional(errors, fieldErrorMap, raw, label, fieldKey) {
  const err = validatePhoneValue(raw, { required: false, label });
  if (!err) return;
  const id = packetFieldId(fieldKey);
  errors.push({ id, fieldKey, message: err.message });
  fieldErrorMap[fieldKey] = err.message;
}

/**
 * Full validation: structural + explicit messages + phone digit rules.
 * @returns {{ ok: boolean, errors: Array<{id: string, fieldKey: string, message: string}>, fieldErrorMap: Record<string, string>, firstErrorId: string|null }}
 */
export function validateForSubmit(formId, fields, packet) {
  const errors = [];
  const fieldErrorMap = {};
  const p = packet || {};
  const showMother = p.has_mother !== false;
  const showFather = p.has_father !== false;
  const showGuardian = p.has_guardian === true;

  const pushStructural = (fieldKey, message) => {
    const id = packetFieldId(fieldKey);
    errors.push({ id, fieldKey, message });
    fieldErrorMap[fieldKey] = message;
  };

  const fa03Key = (i, part) => `fa03_bus_${i}_${part}`;

  if (formId === 'fa_03') {
    const rows = normalizeFa03BusRows(fields.bus_pickup_rows || []);
    for (let i = 0; i < 2; i += 1) {
      const row = rows[i] || {};
      const fn = (row.first_name || '').trim();
      const ln = (row.last_name || '').trim();
      const legacy = (row.name || '').trim();
      const parts = legacy.split(/\s+/).filter(Boolean);
      const legacyOk = legacy && parts.length >= 2;
      if (!fn && !legacyOk) {
        pushStructural(fa03Key(i, 'first'), `First name for Authorized person ${i + 1} is required`);
      } else if (fn) {
        pushTypedField(errors, fieldErrorMap, fa03Key(i, 'first'), fn, `First name for Authorized person ${i + 1}`, { required: true, kind: 'name' });
      }
      if (!ln && !legacyOk) {
        pushStructural(fa03Key(i, 'last'), `Last name for Authorized person ${i + 1} is required`);
      } else if (ln) {
        pushTypedField(errors, fieldErrorMap, fa03Key(i, 'last'), ln, `Last name for Authorized person ${i + 1}`, { required: true, kind: 'name' });
      }
      const ph = (row.phone || '').trim();
      if (!ph) {
        pushStructural(fa03Key(i, 'phone'), `Phone for Authorized person ${i + 1} is required`);
      } else {
        pushRequiredPhone(errors, fieldErrorMap, ph, `Phone for Authorized person ${i + 1}`, fa03Key(i, 'phone'));
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_04') {
    const hasCell = !!(fields.mother_cell?.trim() || fields.father_cell?.trim());
    if (!hasCell) {
      pushStructural('mother_cell', 'At least one parent cell phone is required (mother or father)');
    } else {
      pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
    }
    if (!isNonBlankText(fields.email)) {
      pushStructural('email', 'Email address is required');
    } else {
      pushTypedField(errors, fieldErrorMap, 'email', fields.email, 'Email address', { required: true, kind: 'email' });
    }
    if (showGuardian) {
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_cell, 'Guardian cell', 'guardian_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_work_phone, 'Guardian work phone', 'guardian_work_phone');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_home_phone, 'Guardian home phone', 'guardian_home_phone');
    }
    // Emergency contacts: EC1 is mandatory; any started EC (2/3) must be complete
    // (no partial contact) — name + relationship + phone all required, never skippable.
    // 55th keeps all three phones mandatory (existing behavior).
    const is55th = getPacketSiteSlug(packet) === '55th-street';
    [1, 2, 3].forEach((idx) => {
      const name = fields[`ec${idx}_name`];
      const relationship = fields[`ec${idx}_relationship`];
      const phone = fields[`ec${idx}_phone`];
      const started = isNonBlankText(name) || isNonBlankText(relationship) || isNonBlankText(phone);
      const mustComplete = idx === 1 || is55th || started;
      if (!mustComplete) {
        pushPhoneOptional(errors, fieldErrorMap, phone, `Emergency contact ${idx} phone`, `ec${idx}_phone`);
        return;
      }
      if (!isNonBlankText(name)) {
        pushStructural(`ec${idx}_name`, `Emergency contact ${idx} name is required`);
      } else {
        pushTypedField(errors, fieldErrorMap, `ec${idx}_name`, name, `Emergency contact ${idx} name`, { required: true, kind: 'name' });
      }
      if (!isNonBlankText(relationship)) {
        pushStructural(`ec${idx}_relationship`, `Emergency contact ${idx} relationship is required`);
      }
      pushRequiredPhone(errors, fieldErrorMap, phone, `Emergency contact ${idx} phone`, `ec${idx}_phone`);
    });
    pushRequiredPhone(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    if (!isNonBlankText(fields.pediatrician_name)) {
      pushStructural('pediatrician_name', 'Pediatrician name is required');
    } else {
      pushTypedField(
        errors,
        fieldErrorMap,
        'pediatrician_name',
        fields.pediatrician_name,
        'Pediatrician name',
        { required: true, kind: 'provider' },
      );
    }
    if (isNonBlankText(fields.dentist_name)) {
      pushTypedField(
        errors,
        fieldErrorMap,
        'dentist_name',
        fields.dentist_name,
        'Dentist name',
        { required: false, kind: 'provider' },
      );
    }
    if (!isNonBlankText(fields.allergies)) {
      pushStructural(
        'allergies',
        "Please list your child's allergies or enter None if there are no known allergies",
      );
    } else {
      pushTypedField(errors, fieldErrorMap, 'allergies', fields.allergies, 'Allergies', { required: true, kind: 'allergy' });
    }
    [
      ['preferred_hospital', 'Preferred hospital', 'provider'],
      ['specialist_name', 'Specialist name', 'provider'],
      ['insurance_id', 'Insurance ID', 'identifier'],
      ['insurance_type', 'Insurance type', 'text'],
      ['medications_school', 'Medications at school', 'text'],
      ['health_additional_notes', 'Additional health notes', 'text'],
    ].forEach(([key, label, kind]) => {
      if (isNonBlankText(fields[key])) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: false, kind });
      }
    });
    pushRequiredConsentAck(
      errors,
      fieldErrorMap,
      fields,
      'emergency_treatment_ack',
      'Emergency medical treatment authorization',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_05') {
    pushPhoneOptional(errors, fieldErrorMap, fields.parent_phone, 'Primary phone', 'parent_phone');
    if (showMother) pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
    if (showFather) pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
    if (showGuardian) {
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_cell, 'Guardian cell', 'guardian_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_work_phone, 'Guardian work phone', 'guardian_work_phone');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_home_phone, 'Guardian home phone', 'guardian_home_phone');
    }
    pushPhoneOptional(errors, fieldErrorMap, fields.ec1_phone, 'Emergency contact 1 phone', 'ec1_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec2_phone, 'Emergency contact 2 phone', 'ec2_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec3_phone, 'Emergency contact 3 phone', 'ec3_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'otc_consent_ack',
      'otc_consent_decline',
      'OTC medication consent',
    );
    [
      ['student_city', 'City', 'city'],
      ['student_address', 'Street address', 'address'],
      ['medicaid_number', 'Medicaid number', 'identifier'],
      ['other_insurance_company', 'Insurance company', 'text'],
      ['other_insurance_policy', 'Policy number', 'identifier'],
      ['allergy_food', 'Food allergy', 'allergy'],
      ['allergy_drug', 'Drug allergy', 'allergy'],
      ['allergy_other', 'Other allergy', 'allergy'],
    ].forEach(([key, label, kind]) => {
      if (isNonBlankText(fields[key])) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: false, kind });
      }
    });
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_07') {
    if (!isNonBlankText(fields.pediatrician_name)) {
      pushStructural('pediatrician_name', 'Pediatrician name is required');
    } else {
      pushTypedField(
        errors,
        fieldErrorMap,
        'pediatrician_name',
        fields.pediatrician_name,
        'Pediatrician name',
        { required: true, kind: 'provider' },
      );
    }
    if (isNonBlankText(fields.dentist_name)) {
      pushTypedField(
        errors,
        fieldErrorMap,
        'dentist_name',
        fields.dentist_name,
        'Dentist name',
        { required: false, kind: 'provider' },
      );
    }
    pushRequiredPhone(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    [
      ['preferred_hospital', 'Preferred hospital', 'provider'],
      ['insurance_id', 'Insurance ID', 'identifier'],
      ['insurance_type', 'Insurance type', 'text'],
    ].forEach(([key, label, kind]) => {
      if (isNonBlankText(fields[key])) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: false, kind });
      }
    });
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'doctor_comm_ack',
      'doctor_comm_decline',
      'Communication with doctors',
    );
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'release_info_ack',
      'release_info_decline',
      'Release of information',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_09') {
    if (fields.remote_learning_decline) {
      const anyService = [
        'remote_classroom',
        'remote_speech',
        'remote_ot',
        'remote_pt',
        'remote_counseling',
      ].some((k) => !!fields[k]);
      if (anyService) {
        pushStructural(
          'remote_learning_decline',
          'Choose specific services or decline all — not both',
        );
      }
    } else if (!fa09RemoteLearningSatisfied(fields)) {
      pushStructural(
        'remote_classroom',
        'Select at least one remote service, or check "I do not consent"',
      );
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_10') {
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'trips_ack',
      'trips_decline',
      'school trips and neighborhood walks',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_11') {
    if (!fields.sc_not_assigned) {
      pushPhoneOptional(errors, fieldErrorMap, fields.sc_phone, 'Service coordinator telephone', 'sc_phone');
      if (isNonBlankText(fields.sc_name)) {
        pushTypedField(errors, fieldErrorMap, 'sc_name', fields.sc_name, 'Service coordinator name', { required: false, kind: 'name' });
      }
      if (isNonBlankText(fields.sc_agency)) {
        pushTypedField(errors, fieldErrorMap, 'sc_agency', fields.sc_agency, 'Agency', { required: false, kind: 'text' });
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_07') {
    if (showMother) {
      if (!isNonBlankText(fields.mother_name)) {
        pushStructural('mother_name', 'Mother name is required');
      } else {
        pushTypedField(errors, fieldErrorMap, 'mother_name', fields.mother_name, 'Mother name', { required: true, kind: 'name' });
      }
      pushPhoneOptional(errors, fieldErrorMap, fields.mother_home_phone, 'Mother home phone', 'mother_home_phone');
    }
    const hasCell = !!(fields.mother_cell?.trim() || fields.father_cell?.trim());
    if (!hasCell) {
      pushStructural('mother_cell', 'At least one parent cell phone is required (mother or father)');
    } else {
      pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
    }
    if (!isNonBlankText(fields.email)) {
      pushStructural('email', 'Email address is required');
    } else {
      pushTypedField(errors, fieldErrorMap, 'email', fields.email, 'Email address', { required: true, kind: 'email' });
    }
    if (showFather) {
      pushPhoneOptional(errors, fieldErrorMap, fields.father_home_phone, 'Father home phone', 'father_home_phone');
    }
    if (showGuardian) {
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_cell, 'Guardian cell', 'guardian_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_work_phone, 'Guardian work phone', 'guardian_work_phone');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_home_phone, 'Guardian home phone', 'guardian_home_phone');
    }
    pushRequiredPhone(errors, fieldErrorMap, fields.ec2_phone, 'Emergency contact 2 phone', 'ec2_phone');
    if (!form07PreferredPhoneSatisfied(fields)) {
      pushStructural(
        'preferred_phone',
        'Preferred phone is required (enter a 10-digit number or a valid mother/father cell)',
      );
    } else {
      const resolvedPreferred = resolveForm07PreferredPhone(fields);
      pushPhoneOptional(errors, fieldErrorMap, resolvedPreferred, 'Preferred phone', 'preferred_phone');
    }
    pushPhoneOptional(errors, fieldErrorMap, fields.home_phone, 'Home phone', 'home_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.mother_work_phone, 'Mother work phone', 'mother_work_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.father_work_phone, 'Father work phone', 'father_work_phone');
    if (!isNonBlankText(fields.ec1_name)) {
      pushStructural('ec1_name', 'Emergency contact 1 name is required');
    } else {
      pushTypedField(errors, fieldErrorMap, 'ec1_name', fields.ec1_name, 'Emergency contact 1 name', { required: true, kind: 'name' });
    }
    pushRequiredPhone(errors, fieldErrorMap, fields.ec1_phone, 'Emergency contact 1 phone', 'ec1_phone');
    if (!isNonBlankText(fields.ec2_name)) {
      pushStructural('ec2_name', 'Emergency contact 2 name is required');
    } else {
      pushTypedField(errors, fieldErrorMap, 'ec2_name', fields.ec2_name, 'Emergency contact 2 name', { required: true, kind: 'name' });
    }
    pushRequiredPhone(errors, fieldErrorMap, fields.ec2_phone, 'Emergency contact 2 phone', 'ec2_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_14') {
    const anyComplete = [1, 2, 3].some((i) => form14PickupSlotComplete(fields, i));
    const anyStarted = [1, 2, 3].some((i) => form14PickupSlotStarted(fields, i));
    if (!anyStarted) {
      pushStructural(
        'p1_name',
        'At least one authorized pickup person (name, relationship, and phone) is required',
      );
      pushStructural('p1_relationship', 'Authorized pickup 1 relationship to child is required');
      pushRequiredPhone(errors, fieldErrorMap, fields.p1_phone, 'Authorized pickup 1 phone', 'p1_phone');
    } else {
      [1, 2, 3].forEach((idx) => {
        if (!form14PickupSlotStarted(fields, idx)) {
          pushPhoneOptional(errors, fieldErrorMap, fields[`p${idx}_phone`], `Authorized pickup ${idx} phone`, `p${idx}_phone`);
          return;
        }
        if (!isNonBlankText(fields[`p${idx}_name`])) {
          pushStructural(`p${idx}_name`, `Authorized pickup ${idx} name is required when this slot is started`);
        } else {
          pushTypedField(
            errors,
            fieldErrorMap,
            `p${idx}_name`,
            fields[`p${idx}_name`],
            `Authorized pickup ${idx} name`,
            { required: true, kind: 'name' },
          );
        }
        if (!isNonBlankText(fields[`p${idx}_relationship`])) {
          pushStructural(
            `p${idx}_relationship`,
            `Authorized pickup ${idx} relationship to child is required`,
          );
        } else {
          pushTypedField(
            errors,
            fieldErrorMap,
            `p${idx}_relationship`,
            fields[`p${idx}_relationship`],
            `Authorized pickup ${idx} relationship`,
            { required: true, kind: 'text' },
          );
        }
        pushRequiredPhone(errors, fieldErrorMap, fields[`p${idx}_phone`], `Authorized pickup ${idx} phone`, `p${idx}_phone`);
      });
      if (!anyComplete) {
        // Per-slot errors already pushed for incomplete started slots.
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_15') {
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'release_consent_ack',
      'release_consent_decline',
      'medical and educational records release',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (isPhiAuthorizationForm(formId)) {
    const opwdd = (fields.opwdd_eligible || '').toString().toLowerCase();
    if (opwdd !== 'yes' && opwdd !== 'no' && opwdd !== 'unsure') {
      pushStructural('opwdd_eligible', 'Please indicate OPWDD eligibility (Yes, No, or Unsure)');
    }
    const respite = (fields.receiving_respite || '').toString().toLowerCase();
    if (respite !== 'yes' && respite !== 'no') {
      pushStructural('receiving_respite', 'Please indicate whether your child currently receives respite services');
    }
    const contact = (fields.contact_after_school || '').toString().toLowerCase();
    if (contact !== 'yes' && contact !== 'no') {
      pushStructural(
        'contact_after_school',
        'Please indicate whether you would like HASC to contact you about after-school services',
      );
    }
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'phi_authorize',
      'phi_decline',
      'PHI disclosure authorization',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_12') {
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'creams_consent_ack',
      'creams_consent_decline',
      'use of creams and lotions',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_13') {
    // A2P SMS/voice boxes are optional — only confirm_agreed is required.
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_18') {
    if (!fields.confirm_policy) {
      pushStructural('confirm_policy', 'Illness policy acknowledgment is required');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_19') {
    // Same required keys as before — clearer parent-facing copy only.
    const form19Required = [
      ['handbook_received_ack', 'Please check acknowledgment 1 (Parent Handbook receipt)'],
      ['handbook_illness_policy_ack', 'Please check acknowledgment 2 (Illness Policy)'],
    ];
    if (getPacketSiteSlug(packet) !== '55th-street') {
      form19Required.push([
        'handbook_therapy_makeup_ack',
        'Please check acknowledgment 3 (therapy makeup sessions)',
      ]);
    }
    form19Required.forEach(([key, message]) => {
      if (!fields[key]) {
        pushStructural(key, message);
      }
    });
  }

  if (formId === 'fa_06') {
    if (!fields.understands_labeled_bottles) {
      pushStructural('understands_labeled_bottles', 'Medication bottle policy acknowledgment is required');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'bs_08') {
    const anyAffirm = bs08HasAffirmative(fields);
    if (anyAffirm && fields.consent_none) {
      pushStructural(
        'consent_none',
        'Choose consent options or "I DO NOT consent" — you cannot select both',
      );
    } else if (!anyAffirm && !fields.consent_none) {
      pushStructural('consent_video', BS08_CONSENT_REQUIRED_MESSAGE);
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_08') {
    const anyAffirm = fa08HasAffirmative(fields);
    if (anyAffirm && fields.consent_none) {
      pushStructural(
        'consent_none',
        'Choose consent options or "I DO NOT consent" — you cannot select both',
      );
    } else if (!anyAffirm && !fields.consent_none) {
      pushStructural('consent_none', 'Select at least one photo/video consent option, or choose I DO NOT consent');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_10') {
    for (const key of FORM10_REQUIRED_TEXT_FIELDS) {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, FORM10_REQUIRED_TEXT_MESSAGES[key]);
      } else {
        pushTypedField(errors, fieldErrorMap, key, fields[key], FORM10_REQUIRED_TEXT_MESSAGES[key], { required: true, kind: 'text' });
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_08') {
    FORM08_REQUIRED_YN_KEYS.forEach(([key, label]) => {
      pushRequiredYesNo(errors, fieldErrorMap, fields, key, label);
    });
    const seizureYes = fields.seizure === 'yes' || fields.seizure === true;
    if (seizureYes && !isNonBlankText(fields.seizure_type_frequency)) {
      pushStructural('seizure_type_frequency', 'Please describe seizure type and frequency');
    }
    const gtubeYes = fields.gtube === 'yes' || fields.gtube === true;
    if (gtubeYes) {
      if (!yesNoAnswered(fields.can_food_by_mouth)) {
        pushRequiredYesNo(errors, fieldErrorMap, fields, 'can_food_by_mouth', 'Can have food by mouth');
      }
      if (!isNonBlankText(fields.gtube_feeding_instructions)) {
        pushStructural('gtube_feeding_instructions', 'G-tube type / feeding instructions are required');
      }
      if (!yesNoAnswered(fields.swallow_study)) {
        pushRequiredYesNo(errors, fieldErrorMap, fields, 'swallow_study', 'Swallow study');
      }
    }
    if (fields.problems_eating_drinking === 'yes') {
      if (!isNonBlankText(fields.problems_eating_drinking_specify)) {
        pushStructural('problems_eating_drinking_specify', 'Please specify the eating/drinking concerns');
      } else {
        pushTypedField(
          errors,
          fieldErrorMap,
          'problems_eating_drinking_specify',
          fields.problems_eating_drinking_specify,
          'Eating/drinking concerns',
          { required: true, kind: 'text' },
        );
      }
    }
    const hearingDeviceYes = fields.assistive_hearing_device === 'yes' || fields.assistive_hearing_device === true;
    if (hearingDeviceYes && !fields.assistive_hearing_right && !fields.assistive_hearing_left) {
      pushStructural('assistive_hearing_right', 'Please indicate Right ear and/or Left ear for the assistive hearing device');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_09') {
    if (!fields.has_no_allergies && !fields.has_allergies) {
      pushStructural('has_allergies', 'Please indicate whether your child has allergies or food restrictions');
    } else if (fields.has_allergies && fields.has_no_allergies) {
      pushStructural('has_allergies', 'Choose either allergies or no known allergies — not both');
    }
    if (fields.has_allergies && !fields.has_no_allergies && !form09AllergyDetailsSatisfied(fields)) {
      pushStructural('allergy_rows', 'List at least one allergen or food restriction when indicating allergies');
    }
    const yn = (fields.food_from_home_yn || '').toString().toLowerCase();
    if (yn !== 'yes' && yn !== 'no') {
      pushStructural('food_from_home_yn', 'Please answer whether your child can only eat foods from home');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_11') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'wm_03') {
    // EC1 mandatory (name + relationship + at least one phone). Any started
    // EC2/EC3 must be complete — no partial contact, phones not skippable.
    [1, 2, 3].forEach((idx) => {
      const name = fields[`ec${idx}_name`];
      const relationship = fields[`ec${idx}_relationship`];
      const homePhone = fields[`ec${idx}_home_phone`];
      const cellPhone = fields[`ec${idx}_cell_phone`];
      const started = isNonBlankText(name) || isNonBlankText(relationship)
        || isNonBlankText(homePhone) || isNonBlankText(cellPhone);
      const mustComplete = idx === 1 || started;
      if (!mustComplete) {
        pushPhoneOptional(errors, fieldErrorMap, homePhone, `Emergency contact ${idx} home phone`, `ec${idx}_home_phone`);
        pushPhoneOptional(errors, fieldErrorMap, cellPhone, `Emergency contact ${idx} cell phone`, `ec${idx}_cell_phone`);
        return;
      }
      if (!isNonBlankText(name)) {
        pushStructural(`ec${idx}_name`, `Emergency contact ${idx} name is required`);
      } else {
        pushTypedField(errors, fieldErrorMap, `ec${idx}_name`, name, `Emergency contact ${idx} name`, { required: true, kind: 'name' });
      }
      if (!isNonBlankText(relationship)) {
        pushStructural(`ec${idx}_relationship`, `Emergency contact ${idx} relationship is required`);
      }
      // At least one phone (cell or home); validate format of each provided.
      if (!isNonBlankText(homePhone) && !isNonBlankText(cellPhone)) {
        pushStructural(`ec${idx}_cell_phone`, `Emergency contact ${idx} phone is required`);
      } else {
        pushPhoneOptional(errors, fieldErrorMap, homePhone, `Emergency contact ${idx} home phone`, `ec${idx}_home_phone`);
        pushPhoneOptional(errors, fieldErrorMap, cellPhone, `Emergency contact ${idx} cell phone`, `ec${idx}_cell_phone`);
      }
    });
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
    [
      ['home_address', 'Home address', 'address'],
      ['home_city', 'Home city', 'city'],
      ['home_state', 'Home state', 'text'],
      ['home_zip', 'Home ZIP', 'zip'],
    ].forEach(([key, label, kind]) => {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, `${label} is required`);
      } else if (kind) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: true, kind });
      }
    });
    [
      ['allergies', 'Allergies', 'allergy'],
      ['insurance_name', 'Insurance name', 'text'],
      ['insurance_id', 'Insurance ID', 'identifier'],
      ['insurance_group', 'Insurance group', 'identifier'],
      ['pediatrician_name', 'Pediatrician name', 'provider'],
      ['medications_detail', 'Medication details', 'text'],
    ].forEach(([key, label, kind]) => {
      if (isNonBlankText(fields[key])) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: false, kind });
      }
    });
    [1, 2, 3].forEach((idx) => {
      const cityKey = `ec${idx}_city`;
      if (isNonBlankText(fields[cityKey])) {
        pushTypedField(errors, fieldErrorMap, cityKey, fields[cityKey], `Emergency contact ${idx} city`, { required: false, kind: 'city' });
      }
    });
    const hasCell = !!(fields.mother_cell?.trim() || fields.father_cell?.trim());
    if (!hasCell) {
      pushStructural('mother_cell', 'At least one parent cell phone is required (mother or father)');
    } else {
      pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother/guardian cell', 'mother_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father/guardian cell', 'father_cell');
    }
    const hasEmail = !!(fields.email?.trim() || fields.father_email?.trim());
    if (!hasEmail) {
      pushStructural('email', 'At least one parent email address is required');
    } else {
      if (isNonBlankText(fields.email)) {
        pushTypedField(errors, fieldErrorMap, 'email', fields.email, 'Mother email', { required: false, kind: 'email' });
      }
      if (isNonBlankText(fields.father_email)) {
        pushTypedField(errors, fieldErrorMap, 'father_email', fields.father_email, 'Father email', { required: false, kind: 'email' });
      }
    }
    const bothEmails = !!(fields.email?.trim() && fields.father_email?.trim());
    if (bothEmails && !['mother', 'father', 'both'].includes(fields.docuseal_recipient)) {
      pushStructural('docuseal_recipient', 'Please select who should receive the signing email');
    }
    if (fields.gender !== 'male' && fields.gender !== 'female') {
      pushStructural('gender', 'Gender (M/F) is required');
    }
    [
      ['mother_name', 'Mother name'],
      ['father_name', 'Father name'],
      ['pediatrician_name', 'Pediatrician name', 'provider'],
    ].forEach(([key, label, kind]) => {
      if (isNonBlankText(fields[key])) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: false, kind: kind || 'name' });
      }
    });
  }

  if (formId === 'wm_05') {
    const anyAffirm = WM05_CONSENT_KEYS.some((k) => !!fields[k]);
    if (anyAffirm && fields.consent_none) {
      pushStructural('consent_none', 'Cannot select both consent and "None"');
    } else if (!anyAffirm && !fields.consent_none) {
      pushStructural('consent_none', 'Please select at least one option or choose "None"');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_06') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_07') {
    if (!fields.no_medications && !isNonBlankText(fields.medication_name)) {
      pushStructural('medication_name', 'Medication name is required unless you select no medications');
    } else if (!fields.no_medications) {
      pushTypedField(errors, fieldErrorMap, 'medication_name', fields.medication_name, 'Medication name', { required: true, kind: 'text' });
      if (!isNonBlankText(fields.medication_dosage)) {
        pushStructural('medication_dosage', 'Medication dosage is required when a medication is listed');
      } else {
        pushTypedField(errors, fieldErrorMap, 'medication_dosage', fields.medication_dosage, 'Medication dosage', { required: true, kind: 'text' });
      }
      if (!isNonBlankText(fields.medication_frequency)) {
        pushStructural('medication_frequency', 'Medication frequency is required when a medication is listed');
      } else {
        pushTypedField(errors, fieldErrorMap, 'medication_frequency', fields.medication_frequency, 'Medication frequency', { required: true, kind: 'text' });
      }
      if (isNonBlankText(fields.medication_2_name)) {
        pushTypedField(errors, fieldErrorMap, 'medication_2_name', fields.medication_2_name, 'Medication 2 name', { required: true, kind: 'text' });
        if (!isNonBlankText(fields.medication_2_dosage)) {
          pushStructural('medication_2_dosage', 'Medication 2 dosage is required when medication 2 is listed');
        }
        if (!isNonBlankText(fields.medication_2_frequency)) {
          pushStructural('medication_2_frequency', 'Medication 2 frequency is required when medication 2 is listed');
        }
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_10') {
    const wm10Required = [
      ['child_last_name', 'Child last name'],
      ['child_first_name', 'Child first name'],
      ['home_address', 'Address'],
      ['home_city', 'Town'],
      ['home_zip', 'ZIP code'],
      ['date_of_birth', 'Child date of birth'],
      ['auth1_name', 'Authorized person 1 name'],
      ['auth1_cell', 'Authorized person 1 cell'],
      ['auth1_vehicle', 'Authorized person 1 vehicle'],
      ['auth1_plate', 'Authorized person 1 license plate'],
    ];
    if (p.has_mother !== false) {
      wm10Required.push(['mother_name', 'Mother/guardian name'], ['mother_cell', 'Mother/guardian cell']);
    }
    if (p.has_father !== false) {
      wm10Required.push(['father_name', 'Father/guardian name'], ['father_cell', 'Father/guardian cell']);
    }
    wm10Required.forEach(([key, label]) => {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, label + ' is required');
      } else {
        const kind = inferFieldKind(key);
        if (kind) {
          pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: true, kind });
        }
      }
    });
    if (fields.gender !== 'male' && fields.gender !== 'female') {
      pushStructural('gender', 'Gender (M/F) is required');
    }
    for (let idx = 2; idx <= 4; idx += 1) {
      const name = fields['auth' + idx + '_name'];
      const cell = fields['auth' + idx + '_cell'];
      const vehicle = fields['auth' + idx + '_vehicle'];
      const plate = fields['auth' + idx + '_plate'];
      const started = [name, cell, vehicle, plate].some((v) => isNonBlankText(v));
      if (!started) continue;
      if (!isNonBlankText(name)) pushStructural('auth' + idx + '_name', 'Authorized person ' + idx + ' name is required');
      if (!isNonBlankText(cell)) pushStructural('auth' + idx + '_cell', 'Authorized person ' + idx + ' cell is required');
      else pushPhoneOptional(errors, fieldErrorMap, cell, 'Authorized person ' + idx + ' cell', 'auth' + idx + '_cell');
      if (!isNonBlankText(vehicle)) pushStructural('auth' + idx + '_vehicle', 'Authorized person ' + idx + ' vehicle is required');
      if (!isNonBlankText(plate)) pushStructural('auth' + idx + '_plate', 'Authorized person ' + idx + ' license plate is required');
    }
    pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother/guardian cell', 'mother_cell');
    pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father/guardian cell', 'father_cell');
    pushRequiredPhone(errors, fieldErrorMap, fields.auth1_cell, 'Authorized person 1 cell', 'auth1_cell');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Acknowledge the information above and that you will inform HASC of any changes');
    }
  }

  if (formId === 'wm_12') {
    if (!isNonBlankText(fields.allergies)) {
      pushStructural('allergies', 'Allergies are required — enter None if there are no known allergies');
    } else {
      pushTypedField(errors, fieldErrorMap, 'allergies', fields.allergies, 'Allergies', { required: true, kind: 'allergy' });
    }
    ['special_diet', 'medical_surgical', 'birthmarks_scars'].forEach((key) => {
      if (isNonBlankText(fields[key])) {
        pushTypedField(errors, fieldErrorMap, key, fields[key], key, { required: false, kind: 'text' });
      }
    });
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_09' || formId === 'wm_13') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
    pushConsentAckDecline(errors, fieldErrorMap, fields, 'medicaid_consent_ack', 'medicaid_consent_decline', 'Medicaid consent');
  }

  if (formId === 'wm_04' || formId === 'wm_06' || formId === 'wm_11') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_04') {
    const wm04Required = [
      ['student_name', 'Child name'],
      ['home_address', 'Address'],
      ['insurance_name', 'Medical coverage (insurance plan name)'],
      ['ec1_name', 'Emergency contact'],
      ['parent_name', 'Authorized signature name'],
      ['ec1_relationship', 'Relationship to child'],
    ];
    wm04Required.forEach(([key, label]) => {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, `${label} is required`);
      } else {
        const kind = inferFieldKind(key);
        if (kind) {
          pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: true, kind });
        }
      }
    });
    pushPhoneOptional(errors, fieldErrorMap, fields.home_phone, 'Home phone', 'home_phone');
    if (fields.gender !== 'male' && fields.gender !== 'female') {
      pushStructural('gender', 'Gender (M/F) is required');
    }
  }

  if (formId === 'wm_01') {
    const wm01Required = [
      ['parent_name', 'Parent/guardian name'],
      ['student_name', 'Child name'],
      ['alert_cell_phone', 'Cell phone'],
      ['email', 'Email address'],
    ];
    wm01Required.forEach(([key, label]) => {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, `${label} is required`);
      } else {
        const kind = inferFieldKind(key);
        if (kind) {
          pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: true, kind });
        }
      }
    });
    pushPhoneOptional(errors, fieldErrorMap, fields.home_phone, 'Home phone', 'home_phone');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_12') {
    for (const key of FA12_REQUIRED_YESNO_KEYS) {
      if (!isRequiredYesNoSatisfied(key, fields[key])) {
        const message = FA12_YESNO_NA_ALLOWED_KEYS.has(key)
          ? 'Please answer Yes, No, or N/A'
          : 'Please answer Yes or No';
        pushStructural(key, message);
      }
    }
    for (const [key, message] of FA12_REQUIRED_TEXT_FIELDS) {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, message);
      } else {
        const label = message.replace(' is required', '');
        const kind = inferFieldKind(key);
        if (kind === 'phone') {
          pushRequiredPhone(errors, fieldErrorMap, fields[key], label, key);
        } else if (kind) {
          pushTypedField(errors, fieldErrorMap, key, fields[key], label, { required: true, kind });
        }
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural(
        'confirm_agreed',
        'Please check the acknowledgment to continue',
      );
    }
  }

  if (formId === 'fa_16' || formId === 'fa_17') {
    const requiredYesNo = formId === 'fa_16' ? FA16_REQUIRED_YESNO_KEYS : FA17_REQUIRED_YESNO_KEYS;
    for (const key of requiredYesNo) {
      if (!isRequiredYesNoSatisfied(key, fields[key])) {
        const message = FA16_YESNO_NA_ALLOWED_KEYS.has(key)
          ? 'Please answer Yes, No, or N/A'
          : 'Please answer Yes or No';
        pushStructural(key, message);
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural(
        'confirm_agreed',
        'Please check the acknowledgment to continue',
      );
    }
  }

  if (fourteenthAveInfoAckRequired(formId, getPacketSiteSlug(packet))) {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  const ok = errors.length === 0;
  const firstErrorId = errors.length ? errors[0].id : null;
  return { ok, errors, fieldErrorMap, firstErrorId };
}

/** Caption under "Your responses" when the form includes required parent controls (incl. accuracy checkbox). */
export function shouldShowRequiredFieldsCaption(hasParentFields, informational) {
  return !!hasParentFields && !informational;
}
